/**
 * Formatting for the date-only (`YYYY-MM-DD`) fields the trial API returns -- `trial.endsAt`,
 * `scheduledSubscription.startsOn`.
 *
 * Why this exists rather than `new Date(iso).toLocaleDateString(...)`: a bare date string is parsed
 * by the ECMAScript spec as UTC midnight, while `toLocaleDateString` renders in local time. Every
 * timezone west of Greenwich therefore renders `2027-01-15` as "14 Jan 2027" -- a trial that the
 * server, the emails and the admin console all say ends on the 15th. The string is a calendar date
 * with no time and no zone, so it is parsed as plain digits and never routed through `Date`.
 *
 * Output is the fixed "15 Jan 2027" form from the approved mockup (not locale-dependent): the
 * surrounding copy is English-only, and a locale-shifted month name would desync from it.
 */

const MONTH_ABBREVIATIONS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})/;

/** `"2027-01-15"` -> `"15 Jan 2027"`. Returns "" for null/undefined, and the input unchanged if it
 *  is not a date we recognise -- a trial banner must never render "Invalid Date" at a teacher. */
export function formatTrialDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const match = DATE_ONLY.exec(iso);
  if (!match) return iso;
  const [, year, month, day] = match;
  const monthLabel = MONTH_ABBREVIATIONS[Number(month) - 1];
  if (!monthLabel) return iso;
  return `${Number(day)} ${monthLabel} ${year}`;
}

/**
 * "42 days left" / "1 day left" / "last day" -- the server's `daysRemaining`, pluralised. Never
 * derived from the clock: see `TeacherTrialResponse.daysRemaining`.
 */
export function formatDaysLeft(daysRemaining: number): string {
  if (daysRemaining <= 0) return "last day";
  if (daysRemaining === 1) return "1 day left";
  return `${daysRemaining} days left`;
}
